import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";
import glassFacade from "@/assets/glass-facade.jpg";
import glassDoors from "@/assets/glass-doors.jpg";
import glassPartitions from "@/assets/glass-partitions.jpg";
import glassTempered from "@/assets/glass-tempered.jpg";
import glassInsulated from "@/assets/glass-insulated.jpg";
import glassDecorative from "@/assets/glass-decorative.jpg";
import glassMirrors from "@/assets/glass-mirrors.jpg";
import glassTables from "@/assets/glass-tables.jpg";

const GlassProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      backToProducts: "العودة لمنتجات الزجاج",
      contactUs: "تواصل معنا للطلب",
      features: "المميزات",
      ourProjects: "مشاريعنا وأعمالنا",
      products: {
        "facades": {
          title: "واجهات زجاجية",
          description: "واجهات زجاجية للمباني والأبراج بتصاميم عصرية",
          longDescription: "نقدم واجهات زجاجية للمباني والأبراج بتصاميم عصرية تضفي مظهراً أنيقاً وفاخراً. توفر إضاءة طبيعية وإطلالات رائعة مع عزل حراري متميز ومقاومة للعوامل الجوية.",
          features: ["إضاءة طبيعية", "عزل حراري", "مظهر فاخر", "مقاومة العوامل الجوية", "صيانة سهلة"],
          images: [glassFacade, glassDoors, glassPartitions]
        },
        "doors": {
          title: "أبواب سيكوريت",
          description: "أبواب سيكوريت للمداخل والمحلات التجارية",
          longDescription: "أبواب سيكوريت من الزجاج المقوى عالي الأمان للمداخل والمحلات التجارية. تصاميم أنيقة ومتينة مع إمكانية التحكم الآلي والإغلاق الذاتي.",
          features: ["زجاج مقوى", "أمان عالي", "إغلاق ذاتي", "تصاميم أنيقة", "تحكم آلي"],
          images: [glassDoors, glassFacade, glassPartitions]
        },
        "partitions": {
          title: "قواطع زجاجية",
          description: "قواطع وفواصل زجاجية للمكاتب والمساحات التجارية",
          longDescription: "قواطع وفواصل زجاجية للمكاتب والمساحات التجارية توفر الخصوصية مع الحفاظ على الإضاءة الطبيعية. خيارات متعددة من الزجاج الشفاف والمعتم والملون.",
          features: ["خصوصية مع إضاءة", "خيارات متعددة", "عزل صوتي", "تصاميم مرنة", "تركيب سريع"],
          images: [glassPartitions, glassFacade, glassDoors]
        },
        "tempered": {
          title: "زجاج معالج حرارياً",
          description: "زجاج معالج حرارياً ومقاوم للكسر",
          longDescription: "زجاج معالج حرارياً ومقاوم للكسر يوفر أعلى مستويات الأمان والسلامة للمباني السكنية والتجارية. آمن عند الكسر حيث يتفتت إلى قطع صغيرة غير حادة.",
          features: ["مقاوم للكسر", "آمان عند الكسر", "قوة عالية", "مقاوم للحرارة", "استخدامات متعددة"],
          images: [glassTempered, glassDoors, glassFacade]
        },
        "insulated": {
          title: "زجاج عازل",
          description: "زجاج عازل للحرارة والصوت",
          longDescription: "زجاج عازل للحرارة والصوت يساهم في توفير الطاقة وتحسين بيئة المعيشة والعمل. تقنية الزجاج المزدوج والثلاثي مع فراغ هوائي أو غاز عازل.",
          features: ["عزل حراري", "عزل صوتي", "توفير الطاقة", "زجاج مزدوج", "راحة أكثر"],
          images: [glassInsulated, glassTempered, glassFacade]
        },
        "decorative": {
          title: "زجاج مزخرف وملون",
          description: "زجاج مزخرف وملون للديكورات الداخلية",
          longDescription: "زجاج مزخرف وملون للديكورات الداخلية يضيف لمسة فنية وجمالية فريدة للمساحات. تشكيلة واسعة من الألوان والنقوش والأنماط الفنية.",
          features: ["ألوان متعددة", "نقوش فنية", "لمسة جمالية", "خصوصية", "إضاءة ملونة"],
          images: [glassDecorative, glassMirrors, glassPartitions]
        },
        "mirrors": {
          title: "مرايا",
          description: "مرايا بتصاميم وأشكال متنوعة",
          longDescription: "مرايا بتصاميم وأشكال متنوعة للمنازل والفنادق والمحلات التجارية. جودة عالية وتشطيبات فاخرة مع إمكانية التصنيع حسب المقاسات المطلوبة.",
          features: ["تصاميم متنوعة", "جودة عالية", "مقاسات مخصصة", "تشطيبات فاخرة", "تركيب احترافي"],
          images: [glassMirrors, glassDecorative, glassTables]
        },
        "tables": {
          title: "طاولات وأسطح زجاجية",
          description: "طاولات وأسطح زجاجية للمنازل والمكاتب",
          longDescription: "طاولات وأسطح زجاجية للمنازل والمكاتب من الزجاج المقوى بتصاميم عصرية وأنيقة. متانة عالية وسهولة في التنظيف مع خيارات متعددة من السماكات والألوان.",
          features: ["زجاج مقوى", "تصاميم عصرية", "سهولة التنظيف", "سماكات متعددة", "ألوان متنوعة"],
          images: [glassTables, glassMirrors, glassPartitions]
        }
      }
    },
    en: {
      backToProducts: "Back to Glass Products",
      contactUs: "Contact Us to Order",
      features: "Features",
      ourProjects: "Our Projects & Work",
      products: {
        "facades": {
          title: "Glass Facades",
          description: "Glass facades for buildings and towers with modern designs",
          longDescription: "We offer glass facades for buildings and towers with modern designs adding an elegant and luxurious appearance. Providing natural lighting and great views with excellent thermal insulation and weather resistance.",
          features: ["Natural lighting", "Thermal insulation", "Luxurious appearance", "Weather resistant", "Easy maintenance"],
          images: [glassFacade, glassDoors, glassPartitions]
        },
        "doors": {
          title: "Securit Doors",
          description: "Securit doors for entrances and commercial shops",
          longDescription: "Securit doors made of high-security tempered glass for entrances and commercial shops. Elegant and durable designs with automatic control and self-closing options.",
          features: ["Tempered glass", "High security", "Self-closing", "Elegant designs", "Automatic control"],
          images: [glassDoors, glassFacade, glassPartitions]
        },
        "partitions": {
          title: "Glass Partitions",
          description: "Glass partitions and dividers for offices and commercial spaces",
          longDescription: "Glass partitions and dividers for offices and commercial spaces providing privacy while maintaining natural lighting. Multiple options of clear, frosted, and colored glass.",
          features: ["Privacy with light", "Multiple options", "Sound insulation", "Flexible designs", "Quick installation"],
          images: [glassPartitions, glassFacade, glassDoors]
        },
        "tempered": {
          title: "Heat-Treated Glass",
          description: "Heat-treated and break-resistant glass",
          longDescription: "Heat-treated and break-resistant glass providing the highest levels of safety and security for residential and commercial buildings. Safe when broken as it shatters into small non-sharp pieces.",
          features: ["Break resistant", "Safe when broken", "High strength", "Heat resistant", "Multiple uses"],
          images: [glassTempered, glassDoors, glassFacade]
        },
        "insulated": {
          title: "Insulated Glass",
          description: "Heat and sound insulating glass",
          longDescription: "Heat and sound insulating glass contributing to energy savings and improved living and working environment. Double and triple glazing technology with air gap or insulating gas.",
          features: ["Thermal insulation", "Sound insulation", "Energy saving", "Double glazing", "More comfort"],
          images: [glassInsulated, glassTempered, glassFacade]
        },
        "decorative": {
          title: "Decorative & Colored Glass",
          description: "Decorative and colored glass for interior decorations",
          longDescription: "Decorative and colored glass for interior decorations adding a unique artistic and aesthetic touch to spaces. Wide selection of colors, patterns, and artistic styles.",
          features: ["Multiple colors", "Artistic patterns", "Aesthetic touch", "Privacy", "Colored lighting"],
          images: [glassDecorative, glassMirrors, glassPartitions]
        },
        "mirrors": {
          title: "Mirrors",
          description: "Mirrors in various designs and shapes",
          longDescription: "Mirrors in various designs and shapes for homes, hotels, and commercial shops. High quality and luxurious finishes with custom size manufacturing capability.",
          features: ["Various designs", "High quality", "Custom sizes", "Luxurious finishes", "Professional installation"],
          images: [glassMirrors, glassDecorative, glassTables]
        },
        "tables": {
          title: "Glass Tables & Surfaces",
          description: "Glass tables and surfaces for homes and offices",
          longDescription: "Glass tables and surfaces for homes and offices made of tempered glass with modern and elegant designs. High durability and easy cleaning with multiple thickness and color options.",
          features: ["Tempered glass", "Modern designs", "Easy to clean", "Multiple thicknesses", "Various colors"],
          images: [glassTables, glassMirrors, glassPartitions]
        }
      }
    }
  };

  const t = content[language];
  const product = slug ? t.products[slug as keyof typeof t.products] : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{language === "ar" ? "المنتج غير موجود" : "Product Not Found"}</h1>
          <Link to="/glass">
            <Button className="bg-primary hover:bg-primary/90">{t.backToProducts}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <Link to="/glass" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 font-kufi">
          <ArrowIcon className="w-4 h-4" />
          {t.backToProducts}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
          <h1 className="text-4xl font-kufi font-bold text-primary mb-4">{product.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{product.description}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-card rounded-xl shadow-lg p-8 mb-12">
          <p className="text-lg leading-relaxed text-card-foreground">{product.longDescription}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-primary/5 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-6">{t.features}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-sm">
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="text-card-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-8 text-center">{t.ourProjects}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.images.map((image, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.1 }} className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]">
                <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="text-center bg-primary/10 rounded-xl p-8">
          <Link to="/contact">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-kufi text-lg px-8">{t.contactUs}</Button>
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default GlassProductDetail;
